import type { Workspace } from '@awork/features/workspace/models/workspace.model'
import { WorkspaceStore } from '@awork/features/workspace/state/workspace.store'
import { getWorkspaceSubdomain } from '@awork/_shared/functions/workspace-subdomain'
import { canSwitchWorkspace } from '../../../auth/functions/can-switch-workspace'
import { MobileLogoutService } from '../../../framework/services/mobile-logout-service/mobile-logout.service'
import { WorkspaceEntryService } from '../../../framework/services/workspace-entry-service/workspace-entry.service'
import { DestroyRef, Injectable, Injector, NgZone, computed, signal, inject } from '@angular/core'
import { WorkspaceQuery } from '@awork/features/workspace/state/workspace.query'
import type { Params } from '@angular/router'
import { Router } from '@angular/router'
import { BrowserService } from '../browser-service/browser.service'
import { MobileNavigationService } from '../mobile-navigation/mobile-navigation.service'
import type { URLOpenListenerEvent } from '@capacitor/app'
import { App } from '@capacitor/app'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import type { Observable } from 'rxjs'
import { catchError, defer, EMPTY, filter, finalize, from, map, of, Subject, switchMap, take } from 'rxjs'
import { Capacitor } from '@capacitor/core'
import type { Project } from '@awork/features/project/models/project.model'
import type { Task } from '@awork/features/task/models/task.model'
import type { TimeTracking } from '@awork/features/time-tracking/models/time-tracking.model'
import { TaskService } from '@awork/features/task/services/task-service/task.service'
import { ProjectService } from '@awork/features/project/services/project-service/project.service'
import { ProjectQuery } from '@awork/features/project/state/project.query'
import { TaskQuery } from '@awork/features/task/state/task.query'
import { guidRegex } from '@awork/_shared/static-data/regular-expressions'
import { ToastService } from '../toast-service/toast.service'
import { generateGUID } from '@awork/_shared/functions/guid-generator'

@Injectable({
  providedIn: 'root'
})
export class DeeplinkService {
  private workspaceQuery = inject(WorkspaceQuery)
  private router = inject(Router)
  private browserService = inject(BrowserService)
  private toastService = inject(ToastService)
  private ngZone = inject(NgZone)
  private readonly mobileNavigationService = inject(MobileNavigationService)
  private projectService = inject(ProjectService)
  private taskService = inject(TaskService)
  private projectQuery = inject(ProjectQuery)
  private taskQuery = inject(TaskQuery)
  private injector = inject(Injector)
  private destroyRef = inject(DestroyRef)
  private readonly workspaceStore = inject(WorkspaceStore)
  private readonly logoutService = inject(MobileLogoutService)
  private readonly workspaceEntryService = inject(WorkspaceEntryService)
  private readonly pendingUrl = signal<URL | null>(null)
  /** The pending link can choose the workspace before login sends credentials. */
  readonly pendingSubdomain = computed(() => this.getPendingSubdomain())
  private readonly readyWorkspace$: Observable<Workspace>
  private incomingUrls = new Subject<string>()

  private guidRegExp = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g
  private guidRegExpString = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}'
  private taskIdentifierRegExp = /[A-Za-z0-9-]+-\d+/
  private taskIdentifierRegExpString = '[A-Za-z0-9-]+-\\d+'
  private projectKeyRegExpString = '[A-Za-z0-9_-]+'
  private taskReferenceRegExpString = `${this.guidRegExpString}|${this.taskIdentifierRegExpString}`

  private projectListUrl = `(projects\\/all)|^\\/projects$`
  private simpleProjectUrl = `projects\\/(${this.guidRegExpString}|${this.projectKeyRegExpString})`
  private complexProjectUrl = `projects\\/(${this.guidRegExpString}|${this.projectKeyRegExpString})\\/details`
  private projectDocumentUrl = `^\\/projects\\/(${this.guidRegExpString}|${this.projectKeyRegExpString})\\/docs\\/(${this.guidRegExpString})\\/?$`

  private taskListUrl = `(tasks\\/(my|all))|^\\/tasks$`
  private simpleTaskUrl = `tasks\\/(${this.guidRegExpString}|${this.taskIdentifierRegExpString})`
  private complexTaskUrl = `tasks\\/(${this.taskReferenceRegExpString})\\/details(?!(:))`
  private taskOutletDetailUrl = `\\/tasks\\/[^?]*\\((?:detail|detailModal|modal|taskModal):(${this.taskReferenceRegExpString})(?:\\/details)?\\)`

  private projectTaskListUrl = `^\\/projects\\/(${this.guidRegExpString}|${this.projectKeyRegExpString})\\/tasks(?:\\/(?:list|old-list|board|timeline))?\\/?$`

  private documentUrl = `^\\/(?:docs(?:\\/(?:favorites|shared))?|documents)\\/(${this.guidRegExpString})\\/?$`
  private aiLandingUrl = `^\\/(?:ai|agents)\\/?$`
  private aiThreadUrl = `^\\/(?:ai|agents)\\/(?:thread\\/)?(${this.guidRegExpString})\\/?$`

  private timeEntriesUrl = `(time-tracking|timeentries\\/my-day)|^\\/(time-tracking|timeentries)$`

  private resetPasswordUrl = `reset-password?`
  private acceptInviteUrl = `accept-invitation?`
  private dashboardUrl = `my/dashboard`

  private isInitialized = false

  private fetchEntity$: Observable<Task | Project | TimeTracking>
  private selectEntity$: Observable<Task | Project | TimeTracking>
  private navigationQueryParams: Params = {}

  constructor() {
    const workspace = this.workspaceQuery.queryCurrentWorkspace()

    this.readyWorkspace$ = defer(() => this.mobileNavigationService.waitUntilReady()).pipe(map(() => workspace()))

    this.incomingUrls
      .pipe(
        switchMap(eventUrl =>
          defer(() => {
            const url = new URL(eventUrl)

            if (!this.isInAppUrl(url.pathname)) {
              this.browserService.showBrowser(url.href)

              return EMPTY
            }

            this.pendingUrl.set(url)

            const isNativeAppUrl = this.isNativeAppUrl(url)
            const queryParams: Params = Object.fromEntries(url.searchParams)

            if (isNativeAppUrl && typeof queryParams['widgetAction'] === 'string') {
              queryParams['widgetInvocation'] = generateGUID()
            }

            if (this.isPasswordUrl(url.pathname) || this.isInviteUrl(url.pathname)) {
              return this.getNavigationParams(url.pathname).pipe(
                switchMap(params => this.navigate(params, queryParams, false))
              )
            }

            return this.openWorkspaceLink(url, queryParams)
          }).pipe(
            finalize(() => this.pendingUrl.set(null)),
            catchError(error => {
              this.handleNavigationError(eventUrl, error)
              return EMPTY
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe()
  }

  /**
   * Gets the workspace named by the pending link.
   * @withSignal {Signal<URL | null>} pendingUrl
   * @returns {string | null}
   */
  private getPendingSubdomain(): string | null {
    const url = this.pendingUrl()

    return url && !this.isNativeAppUrl(url) ? getWorkspaceSubdomain(url.hostname) : null
  }

  /**
   * Selects an eligible pending-link workspace before authentication.
   * @param {Workspace[]} workspaces Workspaces for the account being used to log in.
   * @returns {Workspace | undefined} The workspace selected by the link.
   */
  selectPendingWorkspace(workspaces: Workspace[]): Workspace | undefined {
    const subdomain = this.pendingSubdomain()
    const workspace = this.findWorkspace(workspaces, subdomain)

    if (!workspace || !canSwitchWorkspace(workspace)) {
      return undefined
    }

    this.workspaceStore.setCurrentWorkspace(workspace.id)

    return workspace
  }

  /**
   * Resolves the link workspace before fetching any entity.
   * @param {URL} url
   * @param {Params} queryParams
   * @returns {Observable<unknown>}
   */
  private openWorkspaceLink(url: URL, queryParams: Params): Observable<unknown> {
    return this.readyWorkspace$.pipe(
      take(1),
      switchMap(current => {
        const subdomain = this.isNativeAppUrl(url) ? null : getWorkspaceSubdomain(url.hostname)

        if (!subdomain || this.findWorkspace([current], subdomain)) {
          return this.getNavigationParams(url.pathname).pipe(switchMap(params => this.navigate(params, queryParams)))
        }

        return this.workspaceEntryService.enter({
          find: workspaces => this.findWorkspace(workspaces, subdomain),
          label: () => url.hostname,
          lookupFailed: q.translations.DeeplinkService.workspaceLookupFailed,
          logout: () => this.logoutService.logout(),
          entered: () =>
            this.getNavigationParams(url.pathname).pipe(switchMap(params => this.navigate(params, queryParams))),
          retry: () => this.openWorkspaceLink(url, queryParams)
        })
      })
    )
  }

  /**
   * Finds a workspace by any of its subdomains, ignoring case.
   * @param {Workspace[]} workspaces
   * @param {string | null} subdomain
   * @returns {Workspace | undefined}
   */
  private findWorkspace(workspaces: Workspace[], subdomain: string | null): Workspace | undefined {
    return workspaces.find(workspace =>
      workspace.subdomains.some(sub => sub.name.toLowerCase() === subdomain?.toLowerCase())
    )
  }

  /** Registers link events and recovers the URL that launched the native app. */
  async initDeepLinking(): Promise<void> {
    if (this.isInitialized) {
      return
    }

    this.isInitialized = true

    let receivedStartupEvent = false
    let readingLaunchUrl = true
    const listener = await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      if (readingLaunchUrl) {
        receivedStartupEvent = true
      }

      this.ngZone.run(() => this.incomingUrls.next(event.url))
    })

    if (this.destroyRef.destroyed) {
      await listener.remove()

      return
    }

    this.destroyRef.onDestroy(() => void listener.remove())

    if (Capacitor.isNativePlatform()) {
      const launch = await App.getLaunchUrl()

      if (launch && !receivedStartupEvent) {
        this.ngZone.run(() => this.incomingUrls.next(launch.url))
      }
    }

    readingLaunchUrl = false
  }

  /**
   * Returns an observable with navigation properties
   * based on the path or the current url
   * @param {string} path
   * @returns {Observable<(string | { id: string; })[]>}
   */
  getNavigationParams(path: string): Observable<(string | { id: string })[]> {
    this.fetchEntity$ = undefined
    this.selectEntity$ = undefined
    this.navigationQueryParams = {}

    const projectDocumentMatch = path.match(new RegExp(this.projectDocumentUrl))

    if (projectDocumentMatch) {
      const projectIdOrKey = projectDocumentMatch[1]
      const documentId = projectDocumentMatch[2]

      return this.resolveProjectId(projectIdOrKey).pipe(map(projectId => ['/projects', projectId, 'docs', documentId]))
    }

    const documentMatch = path.match(new RegExp(this.documentUrl))

    if (documentMatch) {
      return of(['/docs', documentMatch[1]])
    }

    const aiThreadMatch = path.match(new RegExp(this.aiThreadUrl))

    if (aiThreadMatch) {
      this.navigationQueryParams = { threadId: aiThreadMatch[1] }

      return of(['/assistant/chat'])
    }

    if (this.isAiLandingUrl(path)) {
      return of(['/assistant/chat'])
    }

    if (this.isTaskDetailUrl(path)) {
      const taskIdOrIdentifier = this.getTaskIdOrIdentifierFromUrl(path)

      if (taskIdOrIdentifier) {
        return this.resolveTaskId(taskIdOrIdentifier).pipe(map(taskId => ['/my-tasks', { id: taskId }]))
      }

      return of(['/my-tasks'])
    }

    if (this.isTaskListUrl(path)) {
      if (path.startsWith('/projects')) {
        const projectIdOrKey = this.getProjectIdOrKeyFromUrl(path)

        if (projectIdOrKey) {
          return this.resolveProjectId(projectIdOrKey).pipe(map(projectId => ['/projects', projectId]))
        }
      }

      return of(['/my-tasks'])
    }

    if (this.isProjectListUrl(path)) {
      return of(['/projects'])
    }

    if (this.isProjectDetailUrl(path)) {
      const projectIdOrKey = this.getProjectIdOrKeyFromUrl(path)

      if (projectIdOrKey) {
        return this.resolveProjectId(projectIdOrKey).pipe(map(projectId => ['/projects', projectId]))
      }

      return of(['/projects'])
    }

    if (this.isTimeEntriesUrl(path)) {
      return of(['/timer'])
    }

    if (this.isPasswordUrl(path)) {
      return of(['/reset-password'])
    }

    if (this.isInviteUrl(path)) {
      return of(['/accept-invitation'])
    }

    if (this.isDashboardUrl(path)) {
      return of(['/my-tasks'])
    }

    return of([])
  }

  /**
   * Resolves task identifier to GUID. If already a GUID, returns it directly.
   * Also sets up fetchEntity$ and selectEntity$ for the navigation flow.
   * @param {string} taskIdOrIdentifier
   * @returns {Observable<string>}
   */
  private resolveTaskId(taskIdOrIdentifier: string): Observable<string> {
    const isGuid = guidRegex.test(taskIdOrIdentifier)

    if (isGuid) {
      const cachedTask = this.taskQuery.getTask(taskIdOrIdentifier)

      this.fetchEntity$ = cachedTask ? of(cachedTask) : defer(() => this.taskService.fetchTask(taskIdOrIdentifier))
      this.selectEntity$ = toObservable(this.taskQuery.queryTask(taskIdOrIdentifier), { injector: this.injector })

      return of(taskIdOrIdentifier)
    }

    const taskFromStore = this.taskQuery.getTaskByIdentifier(taskIdOrIdentifier, false)

    if (taskFromStore) {
      this.fetchEntity$ = of(taskFromStore)
      this.selectEntity$ = toObservable(this.taskQuery.queryTask(taskFromStore.id), { injector: this.injector })

      return of(taskFromStore.id)
    }

    // The key lookup doesn't add the task to the store, so fetch it by ID like a GUID link.
    // Otherwise navigation waits forever for the task to appear in the store.
    return this.taskService.fetchTaskIdByIdentifier(taskIdOrIdentifier).pipe(
      map(taskId => {
        if (taskId) {
          this.fetchEntity$ = defer(() => this.taskService.fetchTask(taskId))
          this.selectEntity$ = toObservable(this.taskQuery.queryTask(taskId), { injector: this.injector })
        }

        return taskId
      })
    )
  }

  /**
   * Resolves project key to GUID. If already a GUID, returns it directly.
   * Also sets up fetchEntity$ and selectEntity$ for the navigation flow.
   * @param {string} projectIdOrKey
   * @returns {Observable<string>}
   */
  private resolveProjectId(projectIdOrKey: string): Observable<string> {
    const isGuid = guidRegex.test(projectIdOrKey)

    if (isGuid) {
      const cachedProject = this.projectQuery.getProject(projectIdOrKey)

      this.fetchEntity$ = cachedProject
        ? of(cachedProject)
        : defer(() => this.projectService.fetchProject(projectIdOrKey, false))
      this.selectEntity$ = this.projectQuery.selectProject(projectIdOrKey, this.injector)

      return of(projectIdOrKey)
    }

    const projectFromStore = this.projectQuery.getProjectByKey(projectIdOrKey, false)

    if (projectFromStore) {
      this.fetchEntity$ = of(projectFromStore)
      this.selectEntity$ = this.projectQuery.selectProject(projectFromStore.id, this.injector)

      return of(projectFromStore.id)
    }

    return this.projectService.getProjectIdByKey(projectIdOrKey, false).pipe(
      switchMap(projectId => {
        if (projectId) {
          this.fetchEntity$ = defer(() => this.projectService.fetchProject(projectId, false))
          this.selectEntity$ = this.projectQuery.selectProject(projectId, this.injector)

          return of(projectId)
        }

        return of(null)
      })
    )
  }

  /**
   * Gets the task ID or identifier from the URL
   * @param {string} path
   * @returns {string | null}
   */
  private getTaskIdOrIdentifierFromUrl(path: string): string | null {
    const outletMatch = path.match(new RegExp(this.taskOutletDetailUrl))

    if (outletMatch) {
      return outletMatch[1]
    }

    const directTaskMatch = path.match(
      new RegExp(`\\/tasks\\/(${this.taskReferenceRegExpString})(?:\\/details)?(?:\\/|$)`)
    )

    if (directTaskMatch) {
      return directTaskMatch[1]
    }

    const identifierMatch = path.match(this.taskIdentifierRegExp)

    if (identifierMatch) {
      return identifierMatch[0]
    }

    return null
  }

  /**
   * Gets the project ID or key from the URL
   * @param {string} path
   * @returns {string | null}
   */
  private getProjectIdOrKeyFromUrl(path: string): string | null {
    const guidMatch = this.getGuidsFromUrl(path)

    if (guidMatch) {
      return guidMatch[0]
    }

    const keyRegex = /projects\/([A-Za-z0-9_-]+)/
    const keyMatch = path.match(keyRegex)

    if (keyMatch) {
      return keyMatch[1]
    }

    return null
  }

  /**
   * Determines if the path of the url can be shown in the app
   * @param {string} path
   * @returns {boolean}
   */
  private isInAppUrl(path: string): boolean {
    return (
      this.isTaskDetailUrl(path) ||
      this.isTaskListUrl(path) ||
      this.isProjectDocumentUrl(path) ||
      this.isDocumentUrl(path) ||
      this.isAiThreadUrl(path) ||
      this.isAiLandingUrl(path) ||
      this.isProjectDetailUrl(path) ||
      this.isProjectListUrl(path) ||
      this.isTimeEntriesUrl(path) ||
      this.isPasswordUrl(path) ||
      this.isInviteUrl(path) ||
      this.isDashboardUrl(path)
    )
  }

  /**
   * Determines if the path targets one document inside a project.
   * @param {string} path
   * @returns {boolean}
   */
  private isProjectDocumentUrl(path: string): boolean {
    return new RegExp(this.projectDocumentUrl).test(path)
  }

  /**
   * Determines if the path targets a global or shared document.
   * @param {string} path
   * @returns {boolean}
   */
  private isDocumentUrl(path: string): boolean {
    return new RegExp(this.documentUrl).test(path)
  }

  /**
   * Determines if the path targets one AI conversation.
   * @param {string} path
   * @returns {boolean}
   */
  private isAiThreadUrl(path: string): boolean {
    return new RegExp(this.aiThreadUrl).test(path)
  }

  /**
   * Determines if the path targets the AI agent landing page.
   * @param {string} path
   * @returns {boolean}
   */
  private isAiLandingUrl(path: string): boolean {
    return new RegExp(this.aiLandingUrl).test(path)
  }

  /**
   * Determines if a URL uses one of the bundle-specific schemes registered by an awork iOS app.
   * @param {URL} url URL received from the native app bridge.
   * @returns {boolean} Whether the URL came from an awork app scheme.
   */
  private isNativeAppUrl(url: URL): boolean {
    return /^io\.awork(?:\.(?:cwork|develop|preview))?:$/.test(url.protocol)
  }

  /**
   * Determines if the url is a task detail url
   * @param {string} path
   * @returns {boolean}
   */
  private isTaskDetailUrl(path: string): boolean {
    const regex = `${this.taskOutletDetailUrl}|${this.complexTaskUrl}|${this.simpleTaskUrl}`
    const regExp = new RegExp(regex)
    const isMatch = regExp.test(path)

    return isMatch
  }

  /**
   * Determines if the url is a task list url
   * @param {string} path
   * @returns {boolean}
   */
  private isTaskListUrl(path: string): boolean {
    const regex = `${this.taskListUrl}|${this.projectTaskListUrl}`
    const regExp = new RegExp(regex)
    const isMatch = regExp.test(path)

    return isMatch
  }

  /**
   * Determines if the url is a project detail url
   * @param {string} path
   * @returns {boolean}
   */
  private isProjectDetailUrl(path: string): boolean {
    const regex = `${this.complexProjectUrl}|${this.simpleProjectUrl}`
    const regExp = new RegExp(regex)
    const isMatch = regExp.test(path)

    return isMatch
  }

  /**
   * Determines if the url is a project list url
   * @param {string} path
   * @returns {boolean}
   */
  private isProjectListUrl(path: string): boolean {
    const regex = `${this.projectListUrl}`
    const regExp = new RegExp(regex)
    const isMatch = regExp.test(path)

    return isMatch
  }

  /**
   * Determines if the url is a time entries url
   * @param {string} path
   * @returns {boolean}
   */
  private isTimeEntriesUrl(path: string): boolean {
    const regex = `${this.timeEntriesUrl}`
    const regExp = new RegExp(regex)
    const isMatch = regExp.test(path)

    return isMatch
  }

  /**
   * Determines if the url is a password url
   * @param {string} path
   * @returns {boolean}
   */
  private isPasswordUrl(path: string): boolean {
    const regex = `${this.resetPasswordUrl}`
    const regExp = new RegExp(regex)
    const isMatch = regExp.test(path)

    return isMatch
  }

  /**
   * Determines if the url is an invitation url
   * @param {string} path
   * @returns {boolean}
   */
  private isInviteUrl(path: string): boolean {
    const regex = `${this.acceptInviteUrl}`
    const regExp = new RegExp(regex)
    const isMatch = regExp.test(path)

    return isMatch
  }

  /**
   * Determines if the url is a dashboard url
   * @param {string} path
   * @returns {boolean}
   */
  private isDashboardUrl(path: string): boolean {
    const regex = `${this.dashboardUrl}`
    const regExp = new RegExp(regex)
    const isMatch = regExp.test(path)

    return isMatch
  }

  /**
   * Gets the guids from the url
   * @param {string} path
   * @returns {RegExpMatchArray}
   */
  private getGuidsFromUrl(path: string): RegExpMatchArray {
    return path.match(this.guidRegExp)
  }

  /**
   * Waits for startup navigation and the requested entity before opening its route.
   * @param {(string | { id: string })[]} navigationParams Target route and matrix parameters.
   * @param {Params} params Query parameters carried by the external URL.
   * @param {boolean} requireWorkspace False for authentication links that work before login.
   * @returns {Observable<boolean>} Completion of the target navigation.
   */
  private navigate(
    navigationParams: (string | { id: string })[],
    params: Params,
    requireWorkspace = true
  ): Observable<boolean> {
    const fetchEntity$ = this.fetchEntity$ || of(undefined)
    const selectEntity$: Observable<unknown> = this.selectEntity$ || of(true)
    const queryParams = { ...params, ...this.navigationQueryParams }

    return this.mobileNavigationService.waitUntilReady(requireWorkspace).pipe(
      switchMap(() => fetchEntity$.pipe(switchMap(() => selectEntity$.pipe(filter(Boolean), take(1))))),
      switchMap(() => this.mobileNavigationService.closeOverlays()),
      filter(Boolean),
      switchMap(() => from(this.router.navigate(navigationParams, { queryParams })))
    )
  }

  /**
   * Reports a failed in-app route and opens the original URL in the system browser.
   * @param {string} url - Original deep link
   * @param {unknown} error - Routing or entity resolution failure
   */
  private handleNavigationError(url: string, error: unknown): void {
    console.error('Could not open mobile deep link', error)
    this.toastService.show(q.translations.DeeplinkService.openFailed, 'warning')
    this.browserService.showBrowser(url)
  }
}
